// src/components/NewAccountNotice.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, X, ArrowRight } from "lucide-react";

const STORAGE_KEY = "pp_migration_notice_seen_v1";

export default function NewAccountNotice() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const alreadySeen = localStorage.getItem(STORAGE_KEY);
    if (!alreadySeen) {
      // Small delay so it doesn't flash before the dashboard finishes loading
      const timer = setTimeout(() => setShow(true), 400);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setShow(false);
  };

  const goToFundWallet = () => {
    dismiss();
    navigate("/dashboard/wallet");
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={dismiss}
    >
      <div
        className="bg-white dark:bg-[#18181b] rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-gray-100 dark:border-white/[0.06]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-violet-500 to-purple-400" />

        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
              <Landmark size={18} className="text-purple-600 dark:text-purple-400" />
            </div>
            <button
              onClick={dismiss}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
            Important: Your funding account has changed
          </h2>

          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
            We've upgraded our payment provider for faster, more reliable transfers.
            Your previous account number is no longer active — please visit{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">Fund Wallet</span>{" "}
            to generate your new dedicated account before making any transfer.
          </p>

          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 rounded-xl px-3 py-2.5 mb-5">
            <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
              Do not send money to your old account number — it will not be credited.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={dismiss}
              className="flex-1 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-white/[0.04] hover:bg-gray-100 dark:hover:bg-white/[0.08] rounded-xl transition"
            >
              Later
            </button>
            <button
              onClick={goToFundWallet}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition"
            >
              Fund Wallet
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}